// ============================================================================
// INVENTORY.JS — Bottom-bar inventory UI for Palm Island
// ============================================================================
import { getInventory } from '../systems/resources.js';
import { RESOURCE_DEFS } from '../systems/resources.js';
import { eventBus, GameEvents } from '../systems/events.js';

let inventoryBar = null;
let tooltipEl = null;
let hideTimer = null;
let tooltipTimer = null;
let isVisible = false;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    #inventory-bar {
      position: fixed;
      bottom: 16px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 850;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 12px;
      padding: 8px 16px;
      font-family: 'Georgia', serif;
      color: #5a3e1b;
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 90vw;
      overflow-x: auto;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.5s ease;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    #inventory-bar::-webkit-scrollbar {
      display: none;
    }

    #inventory-bar.inventory-visible {
      opacity: 1;
      pointer-events: auto;
    }

    .inventory-slot {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-width: 50px;
      cursor: default;
      position: relative;
      flex-shrink: 0;
    }

    .inventory-slot-icon {
      font-size: 24px;
      line-height: 1;
    }

    .inventory-slot-count {
      font-size: 13px;
      font-weight: bold;
      color: #5a3e1b;
      margin-top: 2px;
    }

    #inventory-tooltip {
      position: fixed;
      z-index: 851;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 8px;
      padding: 6px 12px;
      font-family: 'Georgia', serif;
      font-size: 13px;
      color: #5a3e1b;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s ease;
      white-space: nowrap;
      max-width: 250px;
    }

    #inventory-tooltip.tooltip-visible {
      opacity: 1;
    }

    #inventory-tooltip .tooltip-name {
      font-weight: bold;
      margin-bottom: 2px;
    }

    #inventory-tooltip .tooltip-desc {
      font-style: italic;
      font-size: 12px;
      opacity: 0.85;
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// DOM creation
// ---------------------------------------------------------------------------
function createInventoryBar() {
  inventoryBar = document.createElement('div');
  inventoryBar.id = 'inventory-bar';
  document.body.appendChild(inventoryBar);

  tooltipEl = document.createElement('div');
  tooltipEl.id = 'inventory-tooltip';
  document.body.appendChild(tooltipEl);
}

// ---------------------------------------------------------------------------
// Display update
// ---------------------------------------------------------------------------
function updateInventoryDisplay() {
  if (!inventoryBar) return;

  const inventory = getInventory();
  inventoryBar.innerHTML = '';

  let hasAny = false;
  for (const [type, def] of Object.entries(RESOURCE_DEFS)) {
    const count = inventory[type] || 0;
    if (count <= 0) continue;
    hasAny = true;

    const slot = document.createElement('div');
    slot.className = 'inventory-slot';
    slot.dataset.resourceType = type;

    const icon = document.createElement('span');
    icon.className = 'inventory-slot-icon';
    icon.textContent = def.icon;

    const countEl = document.createElement('span');
    countEl.className = 'inventory-slot-count';
    countEl.textContent = count;

    slot.appendChild(icon);
    slot.appendChild(countEl);
    inventoryBar.appendChild(slot);

    // Tooltip on hover (desktop)
    slot.addEventListener('mouseenter', (e) => showTooltip(type, e));
    slot.addEventListener('mouseleave', () => hideTooltip());

    // Tooltip on tap (mobile) — toggle on touchstart
    slot.addEventListener('touchstart', (e) => {
      e.preventDefault();
      showTooltip(type, e);
      // Auto-hide after 2 seconds on mobile
      clearTimeout(tooltipTimer);
      tooltipTimer = setTimeout(hideTooltip, 2000);
    }, { passive: false });
  }

  // If nothing to show, keep bar hidden
  if (!hasAny) {
    hideInventoryBar();
  }
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------
function showTooltip(resourceType, event) {
  const def = RESOURCE_DEFS[resourceType];
  if (!def || !tooltipEl) return;

  tooltipEl.innerHTML = `
    <div class="tooltip-name">${def.icon} ${def.name}</div>
    <div class="tooltip-desc">${def.description}</div>
  `;

  // Position above the slot
  const target = event.currentTarget || event.target;
  const rect = target.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();

  // Temporarily make visible to measure
  tooltipEl.style.visibility = 'hidden';
  tooltipEl.classList.add('tooltip-visible');

  // Use requestAnimationFrame to get accurate measurements after content update
  requestAnimationFrame(() => {
    const updatedRect = tooltipEl.getBoundingClientRect();
    let left = rect.left + rect.width / 2 - updatedRect.width / 2;
    let top = rect.top - updatedRect.height - 8;

    // Clamp to viewport
    left = Math.max(8, Math.min(left, window.innerWidth - updatedRect.width - 8));
    if (top < 8) top = rect.bottom + 8;

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.visibility = '';
  });
}

function hideTooltip() {
  if (!tooltipEl) return;
  tooltipEl.classList.remove('tooltip-visible');
  clearTimeout(tooltipTimer);
}

// ---------------------------------------------------------------------------
// Show / hide logic
// ---------------------------------------------------------------------------
function showInventoryBar() {
  if (!inventoryBar) return;

  isVisible = true;
  inventoryBar.classList.add('inventory-visible');

  // Restart the auto-hide timer
  clearTimeout(hideTimer);
  hideTimer = setTimeout(hideInventoryBar, 5000);
}

function hideInventoryBar() {
  if (!inventoryBar) return;

  isVisible = false;
  inventoryBar.classList.remove('inventory-visible');
  hideTooltip();
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------
export function initInventory() {
  injectStyles();
  createInventoryBar();

  eventBus.on(GameEvents.INVENTORY_CHANGED, () => {
    updateInventoryDisplay();
    showInventoryBar();
  });

  eventBus.on(GameEvents.RESOURCE_COLLECTED, () => {
    showInventoryBar();
  });

  // Show initial state if player already has resources
  updateInventoryDisplay();
  const inventory = getInventory();
  const hasResources = Object.values(inventory).some((c) => c > 0);
  if (hasResources) {
    showInventoryBar();
  }

  console.log('[Inventory] UI initialized');
}
